import React, {
  FC, useEffect, useMemo
} from 'react';
import {
  useSearchParams
} from 'react-router-dom';

import {
  useGetOrgQuestionsManagementActiveQuery,
  useGetOrgQuestionsManagementScoredQuery,
  useGetOrgQuestionsManagementDeactivatedQuery,
} from 'src/redux/openapi';
import {
  usePaginationSettings
} from 'src/shared/components';
import {
  questionManagementFilter, questionTab
} from 'src/shared/utils';
import {
  ScoringTableContent,
  QuestionScoringTabs,
  QuestionScoringFilters,
} from 'src/widgets';

interface QuestionScoringContentProps {
  searchQuery: string;
  organizationId: string;
}

export const QuestionScoringContent: FC<QuestionScoringContentProps> = ({
  searchQuery,
  organizationId,
}) => {
  const [searchParams] = useSearchParams();

  const currentTab = searchParams.get(questionTab) || QuestionScoringTabs.Active;

  const activeFilter = searchParams.get(questionManagementFilter);

  const withFeedback = activeFilter
    ? activeFilter === QuestionScoringFilters.WithFeedback
    : undefined;

  const {
    currentPage, setCurrentPage, pageSize, onPageSizeChange
  } = usePaginationSettings(searchQuery);

  useEffect(
    () => {
      setCurrentPage(1);
    },
    [activeFilter]
  );

  const {
    data: activeData, isLoading: isActiveLoading
  } = useGetOrgQuestionsManagementActiveQuery(
    {
      id: organizationId,
      page: currentPage,
      query: searchQuery,
      limit: pageSize,
    },
    {
      skip: currentTab !== QuestionScoringTabs.Active,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: scoredData, isLoading: isScoredLoading
  } = useGetOrgQuestionsManagementScoredQuery(
    {
      id: organizationId,
      page: currentPage,
      limit: pageSize,
      query: searchQuery,
      withFeedback,
    },
    {
      skip: currentTab !== QuestionScoringTabs.Scored,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: deactivatedData, isLoading: isDeactivatedLoading
  } = useGetOrgQuestionsManagementDeactivatedQuery(
    {
      id: organizationId,
      page: currentPage,
      limit: pageSize,
      query: searchQuery,
    },
    {
      skip: currentTab !== QuestionScoringTabs.Deactivated,
      refetchOnMountOrArgChange: true,
    },
  );

  const data = useMemo(
    () => {
      switch (currentTab) {
        case QuestionScoringTabs.Scored:
          return {
            questions: scoredData?.data.questions || [],
            total: scoredData?.data.total || 0,
          };

        case QuestionScoringTabs.Active:
          return {
            questions: activeData?.data.questions || [],
            total: activeData?.data.total || 0,
          };

        case QuestionScoringTabs.Deactivated:
          return {
            questions: deactivatedData?.data.questions || [],
            total: deactivatedData?.data.total || 0,
          };

        default:
          return {
            questions: [],
            total: 0,
          };
      }
    },
    [currentTab, scoredData, activeData, deactivatedData]
  );

  return (
    <ScoringTableContent
      data={data.questions}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      setCurrentPage={setCurrentPage}
      currentPage={currentPage}
      total={data.total}
      organizationId={organizationId}
      isLoading={isActiveLoading || isScoredLoading || isDeactivatedLoading}
      withSearchQuery={!!searchQuery}
    />
  );
};
